/**
 * DailyReviewComplianceCard
 *
 * Coach-facing dashboard card that shows today's FSRS review compliance for a
 * domain: how many cards were due, how many were completed, compliance %, and
 * an expandable list of at-risk learners.
 *
 * Pulls from GET /scenario-os/adaptive/review-compliance/{domain}.
 * Auto-refreshes every 5 minutes.
 *
 * Props:
 *   domains      — string[] — list of domain IDs the coach can select from
 *   initialDomain — string  — initially selected domain (default: first in list or "all")
 *   onLearnerClick — (learner_id) => void — optional click handler for at-risk rows
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adaptiveAPI } from '../../services/api';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// ── Colour helpers ───────────────────────────────────────────────────────────

function complianceColor(pct) {
  if (pct >= 80) return '#22c55e';   // green
  if (pct >= 50) return '#f59e0b';   // amber
  return '#ef4444';                  // red
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      style={{
        height: 10,
        borderRadius: 99,
        background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
        flexGrow: 1,
      }}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${pct}% complete`}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          background: color,
          borderRadius: 99,
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  );
}

function AtRiskTable({ learners, onLearnerClick }) {
  if (!learners || learners.length === 0) {
    return (
      <div style={{ color: '#22c55e', fontSize: 13, padding: '8px 0' }}>
        ✓ No at-risk learners — everyone has reviewed in the past 3 days.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <th style={thStyle}>Learner</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Days since review</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Due cards</th>
        </tr>
      </thead>
      <tbody>
        {learners.map((l) => (
          <tr
            key={l.learner_id}
            onClick={() => onLearnerClick && onLearnerClick(l.learner_id)}
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              cursor: onLearnerClick ? 'pointer' : 'default',
            }}
          >
            <td style={tdStyle}>{l.name || l.learner_id}</td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#f59e0b' }}>
              {l.days_since_review != null ? `${l.days_since_review}d` : '—'}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>
              {l.due_count}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const thStyle = {
  padding: '6px 8px',
  color: '#94a3b8',
  fontWeight: 600,
  textAlign: 'left',
};

const tdStyle = {
  padding: '7px 8px',
  color: '#e2e8f0',
};

// ── Main component ───────────────────────────────────────────────────────────

export default function DailyReviewComplianceCard({
  domains = [],
  initialDomain,
  onLearnerClick,
}) {
  const domainOptions = domains.length > 0 ? domains : ['all'];
  const [selectedDomain, setSelectedDomain] = useState(
    initialDomain || domainOptions[0],
  );
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const timerRef = useRef(null);

  const fetchCompliance = useCallback(async (domain) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adaptiveAPI.getReviewCompliance(domain);
      setData(res.data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompliance(selectedDomain);
    timerRef.current = setInterval(() => fetchCompliance(selectedDomain), REFRESH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [selectedDomain, fetchCompliance]);

  const pct   = data?.compliance_pct ?? 0;
  const color = complianceColor(pct);

  return (
    <div
      className="coach-section"
      style={{
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.08)',
        padding: '16px 20px',
        marginBottom: 20,
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>📋</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>
            Today's Review Compliance
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Domain selector */}
          {domainOptions.length > 1 && (
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#e2e8f0',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
              }}
              aria-label="Select domain"
            >
              {domainOptions.map((d) => (
                <option key={d} value={d}>
                  {d === 'all' ? 'All domains' : d.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          )}

          {/* Refresh button */}
          <button
            onClick={() => fetchCompliance(selectedDomain)}
            disabled={loading}
            title="Refresh now"
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#94a3b8',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            aria-label="Refresh compliance data"
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      {error ? (
        <div style={{ color: '#f87171', fontSize: 13 }}>⚠ {error}</div>
      ) : loading && !data ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading compliance data…</div>
      ) : data ? (
        <>
          {/* Compliance headline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 24, fontWeight: 800, color }}>
              {pct}%
            </span>
            <span style={{ color: '#94a3b8', fontSize: 13 }}>
              {data.cards_completed_today}/{data.cards_due_today} cards reviewed
              &nbsp;·&nbsp; {data.total_learners} learner{data.total_learners !== 1 ? 's' : ''} due
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <ProgressBar
              value={data.cards_completed_today}
              max={data.cards_due_today}
              color={color}
            />
            <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
              {data.date}
            </span>
          </div>

          {/* At-risk section */}
          <div>
            <button
              onClick={() => setExpanded((x) => !x)}
              style={{
                background: 'none',
                border: 'none',
                color: data.at_risk_learners?.length > 0 ? '#f59e0b' : '#94a3b8',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                padding: '2px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              aria-expanded={expanded}
            >
              {expanded ? '▾' : '▸'}
              At-Risk Learners
              {data.at_risk_learners?.length > 0 && (
                <span
                  style={{
                    background: '#f59e0b',
                    color: '#000',
                    borderRadius: 99,
                    fontSize: 11,
                    fontWeight: 800,
                    padding: '1px 7px',
                    marginLeft: 4,
                  }}
                >
                  {data.at_risk_learners.length}
                </span>
              )}
            </button>

            {expanded && (
              <div style={{ marginTop: 10 }}>
                <AtRiskTable
                  learners={data.at_risk_learners}
                  onLearnerClick={onLearnerClick}
                />
              </div>
            )}
          </div>

          {/* Last refreshed */}
          {lastRefreshed && (
            <div
              style={{
                marginTop: 10,
                fontSize: 11,
                color: '#475569',
                textAlign: 'right',
              }}
            >
              Last refreshed {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              &nbsp;· auto-refreshes every 5 min
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
